import { MessageSchema } from "@redotech/redo-model/agents/llm-messages";
import { unaryStreamRpc } from "@redotech/rpc/definition";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";

// ============================================================================
// Escalation Status
// ============================================================================

export const EscalationStatusSchema = createEnum([
  "waiting",
  "human-active",
  "closed",
]);
export type EscalationStatus = z.infer<typeof EscalationStatusSchema>;

// ============================================================================
// Escalation Record Schema
// ============================================================================

const EscalationRecordSchema_ = z.object({
  _id: zExt.objectId(),
  triageThreadId: z.string(),
  intercomConversationId: z.string().nullable(),
  intercomContactId: z.string().nullable(),
  status: EscalationStatusSchema,
  escalationReason: z.string().nullable(),
  issueCategory: z.string().nullable(),
  noticeTopic: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
  humanJoinedAt: z.date().nullable(),
  closedAt: z.date().nullable(),
});
export const EscalationRecordSchema: z.ZodType<EscalationRecord> =
  EscalationRecordSchema_;
export interface EscalationRecord
  extends z.infer<typeof EscalationRecordSchema_> {}

// ============================================================================
// Human Message Schema (messages from support agents)
// ============================================================================

const HumanMessageSchema_ = z.object({
  id: z.string(),
  body: z.string(),
  authorName: z.string().nullable(),
  authorId: z.string(),
  authorAvatarUrl: z
    .string()
    .nullable()
    .describe("URL of the agent's avatar image"),
  createdAt: z.number().describe("Unix timestamp"),
  attachments: z.array(
    z.object({
      name: z.string().optional(),
      url: z.string(),
      contentType: z.string().optional(),
    }),
  ),
});
export const HumanMessageSchema: z.ZodType<HumanMessage> = HumanMessageSchema_;
export interface HumanMessage extends z.infer<typeof HumanMessageSchema_> {}

// ============================================================================
// Create Escalation
// ============================================================================

export const createEscalation = {
  name: "createEscalation" as const,
  input: z.object({
    triageThreadId: z.string().describe("The AI triage thread ID"),
    chatHistory: z
      .array(MessageSchema)
      .describe("Full AI conversation to send to Intercom"),
    escalationReason: z
      .string()
      .optional()
      .describe("Reason for escalation from AI agent"),
    issueCategory: z
      .string()
      .optional()
      .describe("Category of issue from AI agent"),
  }),
  output: z.object({
    escalationId: z.string(),
    intercomConversationId: z.string(),
    noticeTopic: z
      .string()
      .describe("Topic to subscribe to for real-time updates"),
  }),
};

// ============================================================================
// Get Escalation
// ============================================================================

export const getEscalation = {
  name: "getEscalation" as const,
  input: z
    .object({
      escalationId: zExt.objectId().optional(),
      triageThreadId: z.string().optional(),
    })
    .describe("Provide either escalationId or triageThreadId"),
  output: z.object({ escalation: EscalationRecordSchema.nullable() }),
};

// ============================================================================
// Get Escalation Messages
// ============================================================================

export const getEscalationMessages = {
  name: "getEscalationMessages" as const,
  input: z.object({ escalationId: zExt.objectId() }),
  output: z.object({
    status: EscalationStatusSchema,
    messages: z
      .array(HumanMessageSchema)
      .describe("Messages from human agents"),
  }),
};

// ============================================================================
// Send Escalation Message
// ============================================================================

export const sendEscalationMessage = {
  name: "sendEscalationMessage" as const,
  input: z.object({
    escalationId: zExt.objectId(),
    message: z.string(),
    attachmentUrls: z
      .array(z.string())
      .optional()
      .describe("URLs of attachments to include"),
  }),
  output: z.object({ success: z.boolean(), error: z.string().optional() }),
};

// ============================================================================
// Watch Escalation Events (Streaming)
// ============================================================================

export const EscalationEventTypeSchema = createEnum([
  "human-message",
  "status-change",
  "escalation-closed",
  "connection-made",
  "error",
]);
export type EscalationEventType = z.infer<typeof EscalationEventTypeSchema>;

const EscalationEventSchema_ = z.object({
  type: EscalationEventTypeSchema,
  messageId: z.string().optional(),
  body: z.string().optional(),
  authorName: z.string().nullable().optional(),
  authorId: z.string().optional(),
  authorAvatarUrl: z.string().nullable().optional(),
  createdAt: z.number().optional().describe("Unix timestamp"),
  attachments: z
    .array(
      z.object({
        name: z.string().optional(),
        url: z.string(),
        contentType: z.string().optional(),
      }),
    )
    .optional(),
  status: EscalationStatusSchema.optional(),
  error: z.string().optional(),
});
export const EscalationEventSchema: z.ZodType<EscalationEvent> =
  EscalationEventSchema_;
export interface EscalationEvent
  extends z.infer<typeof EscalationEventSchema_> {}

export const watchEscalationEvents = unaryStreamRpc({
  name: "watchEscalationEvents",
  input: z.object({ escalationId: zExt.objectId() }),
  output: EscalationEventSchema,
});
